import styled from '@emotion/styled'
import {
  CheckboxWithLabel,
  fontSize,
  fontWeight,
  spacing,
  Button,
  SimpleSelect,
  Modal,
  ModalContainer,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Text,
  colors,
} from '@retailer-platform/shared-components'
import React, { type FunctionComponent, useCallback, useEffect, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import { LoadingGenericBase, InformationIcon } from '@instacart/ids-core'
import { Controller, useForm } from 'react-hook-form'
import { type Styles } from 'react-select'
import { css } from '@emotion/react'
import { useAttributeEditAttributes } from 'domains/products/components/view/utils/useAttributeEditAttributes.hooks'
import { useDomainMessages } from 'domains/products/utils/domain/intl'
import { useNotifications } from 'legacy/common/utils/notifications/notifications.hooks'
import { Input } from 'gin-and-tonic/common/input/Input'
import { useCurrentAccountDetails } from 'api/current-account/currentAccount.hooks'
import { OverrideType } from 'domains/products/api'
import { mapFormDataToValueBasedOverrideInput } from 'domains/products/components/view/utils/valueBasedOverrideUtils'
import { ProductSourcesSelect } from 'domains/products/components/view/components/product-sources/ProductSourcesSelect'
import { type AttributeOverrideState } from 'domains/products/components/view/types/attributeOverride.types'
import { type Product } from 'domains/products/components/view/utils/types'
import { useCreateValueBasedOverride } from 'domains/products/api/createValueBasedOverride.hooks'
import { ValueField } from 'domains/products/components/view/components/value-field/ValueField'

interface AttributeEditProps {
  product: Product
  retailerId: string
  onClose: () => void
  onSuccess: () => void
  showRetailerForm?: boolean
  attribute?: string
}

interface OverrideTypeOption {
  label: string
  value: OverrideType
}

const initialState: AttributeOverrideState = {
  attribute: null,
  sourceType: null,
  overrideType: null,
  isPermanent: false,
  expiryDate: null,
  comment: '',
  ticketEntry: '',
  value: '',
  newValue: '',
  permanentAcknowledgement: false,
}

const retailerFormConstants = {
  overrideType: OverrideType.OverrideTypeReplace,
  comment: 'Change made by Retailer on RPP',
}

export const testIds = {
  attributeDropdown: 'attribute-override-attributeDropdown',
  overrideTypeDropdown: 'attribute-override-overrideTypeDropdown',
  expiryType: 'attribute-override-expiryType',
  expiryDate: 'attribute-override-expiryDate',
  inputComment: 'attribute-override-inputComment',
  inputTicket: 'attribute-override-inputTicket',
  inputValue: 'attribute-override-inputValue',
  isPermanentCheckbox: 'attribute-override-isPermanentCheckbox',
  permanentAcknowledgementCheckbox: 'attribute-override-permanentCheckbox',
  saveChangesButton: 'attribute-override-saveChangesButton',
  cancelButton: 'attribute-override-cancelButton',
}

const formNames = {
  attribute: 'attribute',
  source: 'sourceType',
  overrideType: 'overrideType',
  expiryDate: 'expiryDate',
  isPermanent: 'isPermanent',
  comment: 'comment',
  ticket: 'ticketEntry',
  value: 'value',
  newValue: 'newValue',
  permanentAcknowledgement: 'permanentAcknowledgement',
}

export const Divider = styled.div({
  borderBottom: `1px solid ${colors.GRAYSCALE.X20}`,
})

const StyledContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.X16,
  width: '100%',
})

const StyledRow = styled.div({
  display: 'flex',
  flexDirection: 'row',
  width: '100%',
})

const StyledCell = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.X8,
  width: '100%',
})

const StyledLabelCell = css({
  width: '150px',
})

const StyledLabel = css({
  fontSize: fontSize.X12,
  fontWeight: fontWeight.REGULAR,
})

const StyledControls: Styles = {
  control: () => ({
    height: 46,
    fontSize: fontSize.X15,
    '[class*="IndicatorsContainer"]': {
      margin: 'auto',
    },
  }),
}

const StyledInput = css({
  width: 'auto',
  margin: 0,
})

export const AttributeOverride: FunctionComponent<AttributeEditProps> = ({
  product,
  retailerId,
  onClose,
  onSuccess,
  showRetailerForm,
  attribute,
}) => {
  const { handleSubmit, watch, control, register, formState, setValue, unregister } =
    useForm<AttributeOverrideState>({
      mode: 'onChange',
      defaultValues: {
        ...initialState,
        attribute,
      },
    })

  const messages = useDomainMessages({
    title: 'productsDomain.attributeOverride.editAttribute.title',
    attributeLabel: 'productsDomain.attributeOverride.attributes.label',
    attributeSelectPlaceholder: 'productsDomain.attributeOverride.attributes.placeholder',
    sourceLabel: 'productsDomain.attributeOverride.sources.label',
    sourceDescription: 'productsDomain.attributeOverride.sources.description',
    actionLabel: 'productsDomain.attributeOverride.action.label',
    actionDescription: 'productsDomain.attributeOverride.action.description',
    overrideTypePlaceholder: 'productsDomain.attributeOverride.overrideType.placeholder',
    overrideTypeExclude: 'productsDomain.attributeOverride.overrideType.exclude',
    overrideTypeReplace: 'productsDomain.attributeOverride.overrideType.replace',
    editExpiryDateLabel: 'productsDomain.attributeOverride.editExpiryDate.label',
    editExpiryPlaceholder: 'productsDomain.view.tabs.attributeOverrides.list.column.expiryDate',
    expiryIsPermanentLabel: 'productsDomain.attributeOverride.expiryOption.label',
    commentLabel: 'productsDomain.attributeOverride.comment.label',
    commentPlaceholder: 'productsDomain.attributeOverride.comment.placeholder',
    ticketEntryLabel: 'productsDomain.attributeOverride.ticketEntry.label',
    ticketEntryPlaceholder: 'productsDomain.attributeOverride.ticketEntry.placeholder',
    overrideAffectMessage: 'productsDomain.attributeOverride.affect.label',
    acknowledgementLabel: 'productsDomain.attributeOverride.acknowledgement.label',
    loadingErrorMessage: 'productsDomain.attributeOverride.loadingError.message',
    creatOverrideGenericErrorMessage:
      'productsDomain.attributeOverride.createOverride.errorMessage.generic',
    creatOverrideDuplicateEntryErrorMessage:
      'productsDomain.attributeOverride.createOverride.errorMessage.duplicateEntry',
  })

  const { notifyError } = useNotifications()

  const {
    loading: currentAccountLoading,
    error: currentAccountError,
    account: currentAccount,
  } = useCurrentAccountDetails()

  const { createValueBasedOverride, apiSuccess, apiLoading, apiError } =
    useCreateValueBasedOverride()

  // Attribute select
  const { attributes, attributesList, booleanAttributes } = useAttributeEditAttributes()

  // Exclude/replace select
  const overrideTypeOptions: OverrideTypeOption[] = [
    { label: messages.overrideTypeExclude, value: OverrideType.OverrideTypeExclude },
    { label: messages.overrideTypeReplace, value: OverrideType.OverrideTypeReplace },
  ]

  const state = watch()
  const selectedAttribute = state.attribute

  const existingValue = useMemo(() => {
    if (booleanAttributes.has(selectedAttribute)) {
      if (product[selectedAttribute]) {
        return true
      } else {
        return false
      }
    }

    return product[selectedAttribute] || ''
  }, [booleanAttributes, product, selectedAttribute])

  useEffect(() => {
    register('value')
    setValue(formNames.value, existingValue)
    setValue(formNames.newValue, existingValue)
    return () => {
      unregister('value')
    }
  }, [existingValue, register, selectedAttribute, setValue, unregister])

  const getAttributeType = () => {
    const selectedAttribute = state.attribute
    if (!selectedAttribute) {
      return undefined
    }
    return attributes[selectedAttribute].type
  }

  useEffect(() => {
    let errorMessage = ''

    if (!apiError) {
      return
    }

    switch (apiError.graphQLErrors?.[0].extensions?.code) {
      case 'DUPLICATE_VALUE_BASED_OVERRIDE':
        errorMessage = messages.creatOverrideDuplicateEntryErrorMessage
        break
      default:
        errorMessage = messages.creatOverrideGenericErrorMessage
    }

    // dismiss after 15 seconds
    if (errorMessage) {
      notifyError(errorMessage, 'modern', 15000)
    }
  }, [
    apiError,
    messages.creatOverrideDuplicateEntryErrorMessage,
    messages.creatOverrideGenericErrorMessage,
    notifyError,
  ])

  useEffect(() => {
    if (apiSuccess) {
      onSuccess()
    }
  }, [apiSuccess, onSuccess])

  const onLoadingError = useCallback(() => {
    notifyError(messages.loadingErrorMessage, 'modern', 15000)
    onClose()
  }, [messages.loadingErrorMessage, notifyError, onClose])

  useEffect(() => {
    if (currentAccountError) {
      onLoadingError()
    }
  }, [currentAccountError, onLoadingError])

  const enableButton = () => {
    if (state.isPermanent && !state.permanentAcknowledgement) return false
    if (!state.isPermanent && !state.expiryDate) return false

    if (state.overrideType === OverrideType.OverrideTypeReplace) {
      const newValue = state.newValue
      if (newValue === existingValue) {
        return false
      }
    }
    return true
  }

  const onSubmit = (data: AttributeOverrideState) => {
    const formData = {
      ...data,
      ...(showRetailerForm ? retailerFormConstants : {}),
    }

    const mappedData = mapFormDataToValueBasedOverrideInput(
      formData,
      attributes,
      product.productId,
      retailerId,
      currentAccount
    )
    createValueBasedOverride(mappedData)
  }

  return (
    <Modal isOpen={true} maxHeight={1000} maxWidth={500} hideCloseButton={true}>
      <ModalContainer>
        <ModalHeader>{messages.title}</ModalHeader>
        <form onSubmit={handleSubmit(onSubmit)} css={{ width: '100%' }}>
          <ModalBody>
            <StyledContainer>
              <StyledRow>
                <StyledCell css={StyledLabelCell}>
                  <Text weight="semibold" size="medium">
                    {messages.attributeLabel}
                  </Text>
                </StyledCell>
                <StyledCell>
                  <Controller
                    as={SimpleSelect}
                    name={formNames.attribute}
                    control={control}
                    placeholder={messages.attributeSelectPlaceholder}
                    options={attributesList}
                    aria-label={testIds.attributeDropdown}
                    isModernTheme={true}
                    rules={{
                      required: true,
                    }}
                    textSize="large"
                    styles={StyledControls}
                  />
                </StyledCell>
              </StyledRow>

              <StyledRow>
                <StyledCell css={StyledLabelCell} />
                <StyledCell>
                  <Controller
                    as={ValueField}
                    name={formNames.newValue}
                    control={control}
                    disabled={state.overrideType === OverrideType.OverrideTypeExclude}
                    attributeType={getAttributeType()}
                    selectedAttribute={selectedAttribute}
                    currentValue={existingValue}
                  />
                </StyledCell>
              </StyledRow>

              {!showRetailerForm && (
                <StyledRow>
                  <StyledCell css={StyledLabelCell}>
                    <Text weight="semibold" size="medium">
                      {messages.sourceLabel}
                    </Text>
                  </StyledCell>
                  <StyledCell>
                    <Controller
                      as={ProductSourcesSelect}
                      onLoadingError={onLoadingError}
                      name={formNames.source}
                      control={control}
                      rules={{
                        required: true,
                      }}
                      styles={StyledControls}
                    />
                    <label css={StyledLabel} htmlFor={formNames.attribute}>
                      {messages.sourceDescription}
                    </label>
                  </StyledCell>
                </StyledRow>
              )}

              {!showRetailerForm && (
                <StyledRow>
                  <StyledCell css={StyledLabelCell}>
                    <Text weight="semibold" size="medium">
                      {messages.actionLabel}
                    </Text>
                  </StyledCell>
                  <StyledCell>
                    <Controller
                      as={SimpleSelect}
                      name={formNames.overrideType}
                      control={control}
                      placeholder={messages.overrideTypePlaceholder}
                      options={overrideTypeOptions}
                      aria-label={testIds.overrideTypeDropdown}
                      isModernTheme={true}
                      rules={{
                        required: true,
                      }}
                      styles={StyledControls}
                    />
                    <label css={StyledLabel} htmlFor={formNames.attribute}>
                      {messages.actionDescription}
                    </label>
                  </StyledCell>
                </StyledRow>
              )}

              <StyledRow>
                <StyledCell css={StyledLabelCell}>
                  <Text weight="semibold" size="medium">
                    {messages.editExpiryDateLabel}
                  </Text>
                </StyledCell>
                <StyledCell>
                  <Controller
                    as={Input}
                    name={formNames.expiryDate}
                    type="date"
                    outerStyles={StyledInput}
                    placeholder={messages.editExpiryPlaceholder}
                    control={control}
                    id={testIds.expiryDate}
                    data-testid={testIds.expiryDate}
                    disabled={state.isPermanent}
                  />
                  <Controller
                    as={CheckboxWithLabel}
                    name={formNames.isPermanent}
                    control={control}
                    id={testIds.isPermanentCheckbox}
                    label={
                      <Text size="medium" css={StyledLabel}>
                        {messages.expiryIsPermanentLabel}
                      </Text>
                    }
                  />
                </StyledCell>
              </StyledRow>

              {!showRetailerForm && (
                <>
                  <Divider />
                  <StyledRow>
                    <StyledCell css={StyledLabelCell}>
                      <Text weight="semibold" size="medium">
                        {messages.commentLabel}
                      </Text>
                    </StyledCell>
                    <StyledCell>
                      <Controller
                        as={Input}
                        name={formNames.comment}
                        type="text"
                        outerStyles={StyledInput}
                        placeholder={messages.commentPlaceholder}
                        control={control}
                        id={testIds.inputComment}
                        data-testid={testIds.inputComment}
                        rules={{
                          required: true,
                        }}
                      />
                    </StyledCell>
                  </StyledRow>
                  <StyledRow>
                    <StyledCell css={StyledLabelCell}>
                      <Text weight="semibold" size="medium">
                        {messages.ticketEntryLabel}
                      </Text>
                    </StyledCell>
                    <StyledCell>
                      <Controller
                        as={Input}
                        control={control}
                        id={testIds.inputTicket}
                        data-testid={testIds.inputTicket}
                        type="text"
                        name={formNames.ticket}
                        outerStyles={StyledInput}
                        placeholder={messages.ticketEntryPlaceholder}
                      />
                    </StyledCell>
                  </StyledRow>
                </>
              )}

              {state.isPermanent && (
                <StyledRow>
                  <Controller
                    id={testIds.permanentAcknowledgementCheckbox}
                    as={CheckboxWithLabel}
                    name={formNames.permanentAcknowledgement}
                    control={control}
                    label={messages.acknowledgementLabel}
                  />
                </StyledRow>
              )}
            </StyledContainer>
          </ModalBody>
          <ModalFooter>
            {apiLoading && (
              <LoadingGenericBase
                // @ts-expect-error, this accepts a className prop, but it's not properly typed
                css={{ backgroundColor: 'white' }}
              />
            )}
            <StyledContainer>
              <StyledRow>
                <InformationIcon size={24} />
                <Text weight="regular" size="medium" css={{ marginLeft: spacing.X8 }}>
                  {messages.overrideAffectMessage}
                </Text>
              </StyledRow>

              <StyledRow css={{ justifyContent: 'end' }}>
                <Button
                  type="submit"
                  data-testid={testIds.saveChangesButton}
                  disabled={
                    apiLoading || currentAccountLoading || !formState.isValid || !enableButton()
                  }
                >
                  <FormattedMessage id="productsDomain.attributeOverride.saveChanges.button" />
                </Button>
                <Button data-testid={testIds.cancelButton} disabled={apiLoading} onClick={onClose}>
                  <FormattedMessage id="productsDomain.attributeOverride.cancel.button" />
                </Button>
              </StyledRow>
            </StyledContainer>
          </ModalFooter>
        </form>
      </ModalContainer>
    </Modal>
  )
}
